/**
 * Automation API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound AutomationApi.
 * Each tool maps to a method on the C# AutomationApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 60_000; // rule execution can take longer

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface AutomationApiToolSpec {
  /** Method name on the JS-side automationApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  deferred?: boolean;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
}

const TOOL_SPECS: AutomationApiToolSpec[] = [
  // ── Discovery ─────────────────────────────────────────────────────────
  {
    method: "getAvailableActionTypes",
    name: "WS-automationApi-getAvailableActionTypes",
    description:
      "Returns a JSON array of available automation action types. " +
      "Each entry has key, name, and category.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },
  {
    method: "getSelectionTypes",
    name: "WS-automationApi-getSelectionTypes",
    description:
      "Returns a JSON array of selection type options for rule execution. " +
      "Each entry has value, name, and description.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Rule Set listing ──────────────────────────────────────────────────
  {
    method: "getRuleSets",
    name: "WS-automationApi-getRuleSets",
    description:
      "Returns a JSON array of all available rule set files (standard, project, user). " +
      "Each entry includes name, fullPath, fileCategory, ruleCount, and isReadOnly.",
    deferred: false,
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },
  {
    method: "getRules",
    name: "WS-automationApi-getRules",
    description:
      "Returns a JSON array of rules in a given rule set file. " +
      "Each entry includes id, name, description, selected, and ruleSetName.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description:
            "Path to the rule set file. Can be a short name or full path.",
        },
      },
      required: ["ruleSetPath"],
    },
    argOrder: ["ruleSetPath"],
  },
  {
    method: "getRuleDetail",
    name: "WS-automationApi-getRuleDetail",
    description:
      "Returns full detail for a single rule, including the ActionDoc XML. " +
      "Includes id, name, description, selected, ruleSetName, and actionDoc.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule.",
        },
      },
      required: ["ruleSetPath", "ruleId"],
    },
    argOrder: ["ruleSetPath", "ruleId"],
  },

  // ── Rule Set CRUD ─────────────────────────────────────────────────────
  {
    method: "createRuleSet",
    name: "WS-automationApi-createRuleSet",
    description:
      "Creates a new empty rule set file in the user folder. " +
      "Returns JSON with the new rule set info.",
    parameters: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description: "Name for the new rule set.",
        },
      },
      required: ["name"],
    },
    argOrder: ["name"],
  },
  {
    method: "createRule",
    name: "WS-automationApi-createRule",
    description:
      "Creates a new rule in a rule set with the given name, description, and ActionDoc XML. " +
      "Returns JSON RuleInfo for the created rule.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        name: {
          type: "string",
          description: "Name for the new rule.",
        },
        description: {
          type: "string",
          description: "Description for the new rule.",
        },
        actionDocXml: {
          type: "string",
          description:
            "The DevExpress diagram XML defining the rule's action flow.",
        },
      },
      required: ["ruleSetPath", "name", "description", "actionDocXml"],
    },
    argOrder: ["ruleSetPath", "name", "description", "actionDocXml"],
  },
  {
    method: "updateRule",
    name: "WS-automationApi-updateRule",
    description: "Updates the name and description of an existing rule.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to update.",
        },
        name: {
          type: "string",
          description: "New name for the rule.",
        },
        description: {
          type: "string",
          description: "New description for the rule.",
        },
      },
      required: ["ruleSetPath", "ruleId", "name", "description"],
    },
    argOrder: ["ruleSetPath", "ruleId", "name", "description"],
  },
  {
    method: "updateRuleActionDoc",
    name: "WS-automationApi-updateRuleActionDoc",
    description: "Updates the ActionDoc XML of an existing rule.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to update.",
        },
        actionDocXml: {
          type: "string",
          description: "The new DevExpress diagram XML for the rule.",
        },
      },
      required: ["ruleSetPath", "ruleId", "actionDocXml"],
    },
    argOrder: ["ruleSetPath", "ruleId", "actionDocXml"],
  },
  {
    method: "duplicateRule",
    name: "WS-automationApi-duplicateRule",
    description:
      "Duplicates (clones) an existing rule within the same rule set. " +
      "Returns JSON RuleInfo for the new cloned rule.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to duplicate.",
        },
        newName: {
          type: "string",
          description: "Name for the duplicated rule.",
        },
      },
      required: ["ruleSetPath", "ruleId", "newName"],
    },
    argOrder: ["ruleSetPath", "ruleId", "newName"],
  },
  {
    method: "deleteRule",
    name: "WS-automationApi-deleteRule",
    description:
      "Deletes a rule from a rule set. Only works on non-read-only rule sets.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to delete.",
        },
      },
      required: ["ruleSetPath", "ruleId"],
    },
    argOrder: ["ruleSetPath", "ruleId"],
  },
  {
    method: "deleteRuleSet",
    name: "WS-automationApi-deleteRuleSet",
    description:
      "Deletes a user rule set file. Only user (custom) rule sets can be deleted.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file to delete.",
        },
      },
      required: ["ruleSetPath"],
    },
    argOrder: ["ruleSetPath"],
  },

  // ── Execution ─────────────────────────────────────────────────────────
  {
    method: "runRules",
    name: "WS-automationApi-runRules",
    description:
      "Executes selected rules from a rule set. Returns JSON with success, rulesExecuted, " +
      "featuresAffected, featureIds, and errors. " +
      "selectionType can be: AllDataInTheModel, FromDynamicQuery, OnlyChangedData, or FromSegmentEditor.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleIdsJson: {
          type: "string",
          description:
            'JSON array of rule ID strings to execute, e.g. \'["id1","id2"]\'. ' +
            "If empty or null, runs all rules in the set.",
        },
        selectionType: {
          type: "string",
          description:
            "Data selection scope: AllDataInTheModel, FromDynamicQuery, OnlyChangedData, or FromSegmentEditor.",
        },
        skipHistoricData: {
          type: "boolean",
          description: "Whether to skip historic data during execution.",
        },
      },
      required: [
        "ruleSetPath",
        "ruleIdsJson",
        "selectionType",
        "skipHistoricData",
      ],
    },
    argOrder: [
      "ruleSetPath",
      "ruleIdsJson",
      "selectionType",
      "skipHistoricData",
    ],
  },
  {
    method: "runRule",
    name: "WS-automationApi-runRule",
    description:
      "Convenience method: executes a single rule by ID. Returns the same result format as runRules.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to execute.",
        },
        selectionType: {
          type: "string",
          description:
            "Data selection scope: AllDataInTheModel, FromDynamicQuery, OnlyChangedData, or FromSegmentEditor.",
        },
        skipHistoricData: {
          type: "boolean",
          description: "Whether to skip historic data during execution.",
        },
      },
      required: ["ruleSetPath", "ruleId", "selectionType", "skipHistoricData"],
    },
    argOrder: ["ruleSetPath", "ruleId", "selectionType", "skipHistoricData"],
  },

  // ── Validation ────────────────────────────────────────────────────────
  {
    method: "validateRule",
    name: "WS-automationApi-validateRule",
    description:
      "Validates a rule's ActionDoc by attempting to load it. " +
      "Returns JSON with isValid and errors array.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to validate.",
        },
      },
      required: ["ruleSetPath", "ruleId"],
    },
    argOrder: ["ruleSetPath", "ruleId"],
  },

  // ── ActionDoc Builder ──────────────────────────────────────────────────
  {
    method: "getActionDocFormat",
    name: "WS-automationApi-getActionDocFormat",
    description:
      "Returns a JSON description of the ActionDoc XML format and available building blocks. " +
      "Call this BEFORE buildAndSetRuleActionDoc so you know the exact input schema. " +
      "Describes actionType, filterType, featureTypeId, attributeTypeId, attributeValue, " +
      "comparisonOperator, and actionSettings.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },
  {
    method: "buildAndSetRuleActionDoc",
    name: "WS-automationApi-buildAndSetRuleActionDoc",
    description:
      "Builds a valid ActionDoc XML from structured input and sets it on the specified rule. " +
      "Call getActionDocFormat first to see the input schema. " +
      "inputJson schema: { actionType, filterType, featureTypeId?, attributeTypeId?, " +
      "attributeValue?, comparisonOperator?, actionSettings? }. " +
      "actionType: select_feature_action, modify_feature_action, validate_feature_action, create_feature_action. " +
      "filterType: select_all, by_feature_type, by_feature_attribute. " +
      "Returns JSON { success, actionDocXml?, error? }.",
    parameters: {
      type: "object",
      properties: {
        ruleSetPath: {
          type: "string",
          description: "Path to the rule set file.",
        },
        ruleId: {
          type: "string",
          description: "The ID of the rule to set the ActionDoc on.",
        },
        inputJson: {
          type: "string",
          description:
            "JSON-serialized ActionDocBuildInput object. Fields: " +
            "actionType (required, e.g. 'select_feature_action'), " +
            "filterType (required, e.g. 'select_all', 'by_feature_type', 'by_feature_attribute'), " +
            "featureTypeId (int, required for by_feature_type/by_feature_attribute), " +
            "attributeTypeId (int, for by_feature_attribute), " +
            "attributeValue (string, for by_feature_attribute), " +
            "comparisonOperator (Equals, NotEquals, GreaterThan, LessThan), " +
            "actionSettings ({ modifyFeature?, validateFeature? }).",
        },
      },
      required: ["ruleSetPath", "ruleId", "inputJson"],
    },
    argOrder: ["ruleSetPath", "ruleId", "inputJson"],
  },
  {
    method: "buildActionDoc",
    name: "WS-automationApi-buildActionDoc",
    description:
      "Builds ActionDoc XML without saving — useful for previewing. " +
      "Same inputJson schema as buildAndSetRuleActionDoc. " +
      "Returns JSON { success, actionDocXml?, error? }.",
    parameters: {
      type: "object",
      properties: {
        inputJson: {
          type: "string",
          description:
            "JSON-serialized ActionDocBuildInput object (same schema as buildAndSetRuleActionDoc).",
        },
      },
      required: ["inputJson"],
    },
    argOrder: ["inputJson"],
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const automationApi = api?.automationApi;
      if (!automationApi) {
        return JSON.stringify({
          success: false,
          error:
            "AutomationApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = automationApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: automationApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "automationApi",
        automationApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every AutomationApi method.
 * Call this after `CefSharp.BindObjectAsync("automationApi")` has resolved.
 */
export function createAutomationApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: spec.deferred ?? true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(spec.method, spec.argOrder),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
